import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '../utils/apiClient';
import { jwtDecode } from 'jwt-decode';

export const useAuthStore = create(persist(
  (set, get) => ({
    isAuthenticated: false,
    token: null,
    user: null,
    login: async (credentials) => {
      try {
        const response = await apiClient.post("/login", {
          username: credentials.username,
          password: credentials.password
        });

        if (response.mfa_required) {
          return { mfa_required: true, mfa_ticket: response.mfa_ticket };
        }

        const token = response.access_token; // just the JWT, no prefix
        apiClient.setToken(token);

        // Decode JWT to get user info
        let userFromToken = {};
        try {
          const decoded = jwtDecode(token);
          userFromToken = decoded.user || {};
        } catch (e) {
          // fallback: use response.user
        }
        // Compose user object with guaranteed email and id
        const user = {
          ...userFromToken,
          ...response.user,
          email: userFromToken.email || response.user?.email || credentials.username,
          id: userFromToken.id || response.user?.id || userFromToken.email || response.user?.email || credentials.username,
          totp_enabled: response.user?.totp_enabled ?? userFromToken.totp_enabled ?? false,
        };

        set({
          isAuthenticated: true,
          token, // just the JWT
          user
        });

        return token;
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },
    logout: async () => {
      const state = get();
      const token = state.token;
      
      // Call backend logout endpoint to invalidate server-side session
      if (token) {
        try {
          await apiClient.post("/logout");
          console.log('[authStore] Server-side session invalidated');
        } catch (error) {
          console.error('Failed to call backend logout:', error);
          // Continue with client-side cleanup even if backend call fails
        }
      }
      
      // Clear token in API client
      apiClient.setToken(null);
      
      // Import and clear roles to prevent 403 errors after logout
      try {
        // We need to dynamically import to avoid circular dependencies
        const roleStore = require('./roleStore').useRoleStore;
        roleStore.getState().clearRoles();
      } catch (error) {
        console.error('Failed to clear roles during logout:', error);
      }
      
      // Reset authentication state
      set({ isAuthenticated: false, token: null, user: null });
    },
    loginWithSSO: (token, userData) => {
      apiClient.setToken(token);
      const user = {
        ...userData,
        email: userData.email,
        id: userData.id || userData.email,
      };
      set({ isAuthenticated: true, token, user });
    },
    setTotpEnabled: (enabled = true) => {
      const state = get();
      let user = state.user || {};
      let token = state.token;
      // Always decode the JWT to get the full user object
      if (token) {
        let userFromToken = {};
        try {
          const decoded = jwtDecode(token);
          userFromToken = decoded.user || {};
        } catch (e) {}
        user = { ...userFromToken, ...user, totp_enabled: enabled };
      } else {
        user = { ...user, totp_enabled: enabled };
      }
      set({ user });
    },
  }),
  {
    name: 'auth-storage',
    // Bind explicitly to localStorage — survives tab close, new tabs, and
    // browser restart. (Was the zustand v5 default, but making it explicit
    // keeps the deep-link behaviour from quietly changing on a version bump.)
    storage: createJSONStorage(() => localStorage),
    onRehydrateStorage: () => (state) => {
      if (state?.token) {
        apiClient.setToken(state.token.replace('Bearer ', ''));
      }
    },
  }
));
